import cn from 'clsx'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'components/Luxkit/Typography/Heading'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Pane from 'components/Common/Pane'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { rem } from 'polished'
import styled from 'styled-components'
import FormatCurrency from 'components/Common/FormatCurrency'
import TextButton from 'components/Luxkit/Button/TextButton'
import Caption from 'components/Luxkit/Typography/Caption'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { generateTransferCheckoutItem } from 'lib/checkout/experiences/cart'
import { addTransferToCheckout } from 'checkout/lib/analytics/checkoutUAMappers'
import CurrencyContext from 'contexts/currencyContext'
import { addGTMEvent } from 'api/googleTagManager'
import { fetchTransferOptions } from 'actions/ExperienceActions'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ExperienceTransferMultipleBooking from '../ExperienceTransfers/ExperienceTransferMultipleBooking'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { createSelector } from 'reselect'
import getTransferItems from 'checkout/selectors/view/getTransferItems'
import { addToCart, addToCartMultiItem } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'
import Group from 'components/utils/Group'
import { addToUserCart } from 'actions/AuthActions'
import LineShoppingCartIcon from 'components/Luxkit/Icons/line/LineShoppingCartIcon'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'

const FadingElement = styled.div`
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }
`

const TransferContainer = styled(Pane)`
  padding: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.S};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  position: relative;
`

const BottomContent = styled.div`
  display: grid;
  grid-template-areas:
    "save . ."
    "price . buy";
  grid-template-columns: auto 1fr auto;
  grid-row-gap: 4px;
  grid-column-gap: 20px;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-template-areas: "price save buy";
  }
`

const PriceGridItem = styled.div`
  grid-area: price;
`
const SaveGridItem = styled.div`
  grid-area: save;

  ${mediaQueryUp.tablet} {
    text-align: right;
  }
`
const BuyGridItem = styled.div`
  grid-area: buy;
`

const getDefaultTransfers = createSelector(
  (state: App.State) => getTransferItems(state),
  (state: App.State, experienceId: string) => experienceId,
  (checkoutTransfers, experienceId): {
    toAirport?: App.ExperienceTransferView,
    toHotel?: App.ExperienceTransferView,
  } => {
    const existingCheckoutToHotel = checkoutTransfers.find(t => t.experienceId === experienceId && t.transfer.type === 'AIRPORT-TO-HOTEL')?.transfer
    const existingCheckoutToAirport = checkoutTransfers.find(t => t.experienceId === experienceId && t.transfer.type === 'HOTEL-TO-AIRPORT')?.transfer

    return {
      toAirport: existingCheckoutToAirport,
      toHotel: existingCheckoutToHotel,
    }
  },
)

interface Props {
  experienceView: App.ExperienceBookingView;
}

function ExperienceOfferPageTransferSelection(props: Props) {
  const { experienceView } = props
  const experience = experienceView.originalExperience

  const userMultiItemCartEnabled = useAppSelector((state: App.State) => isMultiCartEnabled(state))

  const [submitAction, setSubmitAction] = useState<'buyNow' | 'addToUserCart'>('buyNow')
  const [price, setPrice] = useState<{ total: number; isFromPrice: boolean }>({ total: 0, isFromPrice: true })
  const currency = useContext(CurrencyContext)
  const dispatch = useAppDispatch()
  const transferOptions = useAppSelector(state => state.experience.experienceTransferOptions[experience.id]?.transfers)
  const defaultTransfers = useAppSelector(state => getDefaultTransfers(state, experience.id))
  useEffect(() => {
    dispatch(fetchTransferOptions(experience.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = useCallback((transfers: Array<App.ExperienceTransferView>) => {
    const items = transfers.map(transfer => generateTransferCheckoutItem(experience, transfer))
    transfers.forEach(transfer => addGTMEvent(addTransferToCheckout(experienceView, transfer, currency)))

    if (submitAction === 'addToUserCart') {
      dispatch(addToUserCart(items))
      Analytics.trackEvent(addToCartMultiItem(items, { isMultiCart: true }))
      return
    } else {
      Analytics.trackEvent(addToCart())
    }

    dispatch(addToCheckoutCart(items))
    dispatch(navigateToCheckout())
  }, [currency, dispatch, experience, experienceView, submitAction])

  const onPriceChange = useCallback((total: number, isFromPrice: boolean) => setPrice({ total, isFromPrice }), [])

  const productType = useMemo(() => getAgentHubExperienceType(experience), [experience])

  const handleBuyNowClick = useCallback(() => {
    setSubmitAction('buyNow')
  }, [])

  const handleAddToUserCartClick = useCallback(() => {
    setSubmitAction('addToUserCart')
  }, [])

  return <VerticalSpacer gap={24}>
    <TransferContainer>
      {!transferOptions && <div>
        <LoadingIndicator inline visible>Finding available transfers...</LoadingIndicator>
      </div>}
      {transferOptions && <ExperienceTransferMultipleBooking
        defaultToHotelTransfer={defaultTransfers.toHotel}
        defaultToAirportTransfer={defaultTransfers.toAirport}
        experience={experienceView}
        transferOptions={transferOptions}
        onSubmit={onSubmit}
        onPriceChange={onPriceChange}
        formId="offerPageTransferForm"
      />}
    </TransferContainer>
    <BottomContent>
      <PriceGridItem>
        <FadingElement as={Caption} className={cn({ visible: price.isFromPrice })} variant="medium" colour="neutral-three">
          From
        </FadingElement>
        <Heading variant="heading5">
          <FormatCurrency value={price.total} />{' '}
          <BodyText as="span" variant="medium">
            {!price.isFromPrice && 'total'}
          </BodyText>
        </Heading>
        <PriceRowAgentHubCommission
          offerId={experience.id}
          productType={productType}
          offerLocations={[experience.location.name]}
          size="L"
        />
      </PriceGridItem>
      <TripGuard>
        <SaveGridItem>
          <ExperienceBookmarkButton offer={experience} />
        </SaveGridItem>
      </TripGuard>
      <BuyGridItem>
        <Group direction="vertical-reverse" gap={8} tabletDirection="horizontal">
          {userMultiItemCartEnabled && <TextButton
            kind="secondary"
            startIcon={<LineShoppingCartIcon />}
            size="large"
            type="submit"
            form="offerPageTransferForm"
            onClick={handleAddToUserCartClick}
          >
            Add to cart
          </TextButton>}
          <TextButton
            kind="primary"
            size="large"
            type="submit"
            form="offerPageTransferForm"
            onClick={handleBuyNowClick}
          >
            Buy now
          </TextButton>
        </Group>
      </BuyGridItem>
    </BottomContent>
  </VerticalSpacer>
}

export default ExperienceOfferPageTransferSelection
